import { initializeJwtToken, refreshJwtToken } from '../modules/jwtTokenManager.js';
import { safeStorageGet, safeStorageSet, safeStorageRemove } from '../modules/safe-storage.js';

const WS_DEBUG = false;
const MAX_TOKEN_RETRIES = 5;
const MAX_RETRY_DELAY = 30;
const requestedWebSocketChannels = new Set();

// Every RPC this page may put on the socket. `subscribe` is the channel call
// below; `thread-typing` is the PM sender. websocketerd has no handler for
// anything else.
const ALLOWED_RPC_METHODS = ['subscribe', 'thread-typing'];

/**
 * The one place a frame reaches the socket.
 *
 * @return {boolean} True when the frame was written to an open socket.
 */
function sendRpcFrame(ws, payload) {
    if (!payload || !ALLOWED_RPC_METHODS.includes(payload.method)) {
        return false;
    }

    if (!ws || ws.readyState !== WebSocket.OPEN) {
        return false;
    }

    try {
        ws.send(JSON.stringify(payload));
    } catch (error) {
        wsLog('[WebSocket] Failed to send RPC', error);

        return false;
    }

    return true;
}

function subscribeToChannel(ws, channel) {
    if (!channel) {
        return;
    }

    sendRpcFrame(ws, {
        id: `subscribe:${channel}:${Date.now()}`,
        method: 'subscribe',
        parameters: [channel],
    });
}

function queueWebSocketChannelSubscription(channel) {
    if (!channel) {
        return;
    }

    requestedWebSocketChannels.add(channel);

    // Channels added to the set are subscribed on (re)connect via
    // subscribeToRequestedChannels. This same-tab signal lets a socket that is
    // already open subscribe live, since currentWs is local to $(document).ready.
    document.dispatchEvent(new CustomEvent('ws:subscribe-channel-request', {
        detail: { channel }
    }));
}

window.requestWebSocketChannelSubscription = queueWebSocketChannelSubscription;

// Frame ids are `typing:<threadId>:<ts>`. websocketerd echoes the id in its
// reply, which is how a refusal names the thread whose token it refused.
const TYPING_RPC_ID_PREFIX = 'typing:';

function isTypingRpcReply(data) {
    return typeof data.id === 'string'
        && data.id.startsWith(TYPING_RPC_ID_PREFIX)
        && (data.error !== undefined || data.result !== undefined);
}

/** Only an invalid token is actionable. Every other refusal keeps the token. */
function handleTypingRpcReply(reply) {
    const code = reply.error?.code;
    if (!code) {
        return;
    }

    wsLog('[WebSocket] Typing frame refused', code);

    if (code === 'token_invalid') {
        const threadId = reply.id.split(':')[1];

        if (threadId) {
            window.clearThreadTypingToken?.(threadId);
        }
    }
}

function subscribeToRequestedChannels(ws) {
    requestedWebSocketChannels.forEach((channel) => {
        subscribeToChannel(ws, channel);
    });
}

function wsLog(message, data) {
    if (WS_DEBUG) {
        if (data !== undefined) {
            console.log(message, data);
        } else {
            console.log(message);
        }
    }
}

// Tab title unread indicator: prepends "(N) " to document.title where N is
// the total count of *new* events (PMs, wall posts, friend requests, photo
// comments) that arrived since the user last looked at the
// tab — not the lifetime unread total. Matches Gmail/Slack/Discord: the
// badge answers "what's new since I looked away?", not "how big is my
// backlog?".
//
// "Not actively looking" means either:
//   - The tab is hidden (different tab in this window), or
//   - The window has lost OS focus (different window/app is active)
// The second case matters because Chrome keeps document.hidden=false for the
// front tab of a backgrounded window, so a hidden-only check misses the
// common multi-window workflow.
//
// Implementation: per-counter state. When the user looks away, snapshot the
// current value of each counter as its baseline. The badge shows the sum of
// (latest - baseline) across all tracked counters. When the user returns,
// clear baselines and re-snapshot on next blur.
const TRACKED_COUNTER_IDS = ['pmNewCnt', 'wpNewCnt', 'frNewCnt', 'phNewCnt'];

(function initUnreadTitleIndicator() {
    let baseTitle = null;
    const latestCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, null]));
    const baselineCount = Object.fromEntries(TRACKED_COUNTER_IDS.map((id) => [id, null]));

    // Strip any existing "(N) " prefix so re-renders don't compound.
    const stripPrefix = (title) => title.replace(/^\(\d+\)\s+/, '');

    const captureBaseTitle = () => {
        if (baseTitle === null) {
            baseTitle = stripPrefix(document.title);
        }
    };

    const totalDelta = () => TRACKED_COUNTER_IDS.reduce((sum, id) => {
        const base = baselineCount[id];
        return base === null || latestCount[id] === null ? sum : sum + Math.max(0, latestCount[id] - base);
    }, 0);

    const render = () => {
        if (baseTitle === null) {
            return;
        }
        const delta = totalDelta();
        document.title = delta > 0 ? `(${delta}) ${baseTitle}` : baseTitle;
    };

    // On /messages the page manages its own unread state (and knows whether
    // the user is viewing the active thread), so defer to it entirely.
    const isMessagesPage = () => /\/app\/messages(?:$|\/|\?)/.test(
        window.location.pathname + window.location.search
    );

    const isUserLookingAtTab = () => !document.hidden && document.hasFocus();

    // A SYNC frame repeats an unchanged total, so the baseline must start from the rendered badge, not 0.
    const renderedCount = (id) => {
        const badge = document.querySelector(`[data-counter="${id}"][data-count]`);
        let value = badge ? parseInt(badge.getAttribute('data-count'), 10) : NaN;
        if (!Number.isFinite(value)) {
            const seeds = document.querySelector('[data-counter-seeds]');
            const match = seeds && seeds.getAttribute('data-counter-seeds').match(new RegExp(`(?:^|\\s)${id}:(\\d+)`));
            value = match ? parseInt(match[1], 10) : NaN;
        }
        return Number.isFinite(value) ? value : 0;
    };

    const snapshotBaselinesIfNeeded = () => {
        // Take baseline the first time the user looks away. Subsequent counter
        // updates while still away accumulate against this baseline.
        for (const id of TRACKED_COUNTER_IDS) {
            if (latestCount[id] === null) {
                latestCount[id] = renderedCount(id);
            }
            if (baselineCount[id] === null) {
                baselineCount[id] = latestCount[id];
            }
        }
    };

    const clearAndResetBaselines = () => {
        for (const id of TRACKED_COUNTER_IDS) {
            baselineCount[id] = null;
        }
        // Also halt jq-common's flashTitle setInterval if it's running, so its
        // 1s tick doesn't immediately overwrite the cleared title.
        if (typeof window.stopFlashTitle === 'function') {
            window.stopFlashTitle();
        }
        if (baseTitle !== null) {
            render();
        }
    };

    document.addEventListener('ws:counter-update', (event) => {
        if (!event.detail || !TRACKED_COUNTER_IDS.includes(event.detail.id)) {
            return;
        }
        const value = parseInt(event.detail.value, 10);
        if (!Number.isFinite(value)) {
            return;
        }
        latestCount[event.detail.id] = value;

        if (isMessagesPage() || isUserLookingAtTab()) {
            // User is here — the badge stays empty and the next blur snapshots
            // these latest values as the baselines.
            return;
        }

        captureBaseTitle();
        snapshotBaselinesIfNeeded();
        render();
    });

    document.addEventListener('visibilitychange', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        } else {
            // Tab just went hidden — lock in the baselines so any subsequent
            // arrivals show as a delta from this moment.
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });

    // Window focus/blur covers the case where the tab is the front tab in a
    // background window — visibilityState stays "visible" but the user
    // isn't actually looking at it until the window gains focus.
    window.addEventListener('focus', () => {
        if (isUserLookingAtTab()) {
            clearAndResetBaselines();
        }
    });

    window.addEventListener('blur', () => {
        if (!isUserLookingAtTab()) {
            captureBaseTitle();
            snapshotBaselinesIfNeeded();
        }
    });
})();

/**
 * An inbox box as an integer (ThreadBox: 0 Primary, 1 Requests), or null when
 * the frame carries none. Null rather than a default, so a consumer testing for
 * Requests has to test for it: 0 is a real box and also falsy, and an absent
 * value must never read as either box.
 */
function threadBoxValue(raw) {
    if (typeof raw === 'number') {
        return Number.isInteger(raw) ? raw : null;
    }
    if (typeof raw === 'string' && /^\d+$/.test(raw)) {
        return parseInt(raw, 10);
    }
    return null;
}

/**
 * The browser notification text of a THREAD_NEW_MESSAGE frame. A web tab can
 * render every message type, so it never shows the "update the app" copy that
 * a fallback frame for an old app carries.
 */
function messageNotificationText(data) {
    if (data.snippet) {
        return data.snippet;
    }
    let text = String(data.message || '');
    // The copy for a clip that never plays stays: it is the message, not an update prompt.
    const neverPlays = ['failed', 'unavailable'].includes(data.video_fallback?.status);
    if ((data.video_fallback && !neverPlays) || data.photo_grid_fallback) {
        // Message::getApiData() appends the fallback copy as the last line, after the caption.
        text = text.split('\n').slice(0, -1).join('\n').trim();
    }
    if (text) {
        return text;
    }
    const type = (data.attachments || [])[0]?.type;
    const label = data.video_fallback || type === 'video' ? 'video' : (data.photo_grid_fallback ? 'photo' : type);
    if (!window.t) {
        return '';
    }
    if (label === 'video') {
        return window.t('legacy.pm.snippet_video');
    }
    if (label === 'photo') {
        return window.t('legacy.pm.snippet_photo');
    }
    if (label === 'audio') {
        return window.t('legacy.pm.snippet_audio');
    }
    return '';
}

window["prepareNotification"] = function (entity) {
    // skip THREAD_SYNC_MESSAGE and FEED_SYNC_MESSAGE types
    if (entity.type === 'THREAD_SYNC_MESSAGE' || entity.type === 'FEED_SYNC_MESSAGE') {
        return null;
    }

    let data = entity.data,
        sender = entity.sender,
        cdn = '//ipstatic.net/';

    // default value When Sender is null...
    let thumb = cdn + 'images/male-empty-profile.png';

    let notification = {}

    if (sender) {
        if (sender.avatar) {
            thumb = sender.avatar.thumb_small;
        } else if (sender.sex) {
            thumb = cdn + 'images/' + sender.sex + '-empty-profile.png';
        }

        notification = {
            url: '/' + sender.username,
            username: sender.username,
            img_url: thumb,
            img_dims: "width='50' height='50'",
        };
    }

    /**
     * We don't use some notifications because it's rly hard to support in legacy:
     *
     * FEED_SYNC_MESSAGE
     * THREAD_SYNC_MESSAGE
     */
    switch (entity.type) {
        case 'COMMENT_MESSAGE':
            notification.title = window.tOr('common.push_new_feed_comment', 'New comment on your feed');
            notification.text = data.message;

            return notification;
        case 'COMMENT_REPLY_MESSAGE':
            notification.title = window.tOr('common.push_comment_reply', 'New reply to your comment');
            notification.text = data.message;

            return notification;
        case 'FRIEND_ACCEPT_MESSAGE':
            notification.title = window.tOr('common.push_friend_accepted', sender.username + ' accepted your friendship!', {username: sender.username});
            notification.text = window.tOr('common.push_say_hi', "Say 'Hi'!");

            return notification;
        case 'BIRTHDAY_MESSAGE':
            notification.title = window.tOr('common.push_birthday_today', sender.username + "'s birthday today", {username: sender.username});
            notification.text = window.tOr('common.push_congratulate_birthday', "Congratulate your friend on birthday!");

            return notification;
        case 'FRIEND_JOIN_MESSAGE':
            notification.title = window.tOr('common.push_friend_joined', sender.username + ' just joined', {username: sender.username});
            notification.text = window.tOr('common.push_say_hi', "Say 'Hi'!");

            return notification;
        case 'FRIEND_REQUEST_MESSAGE':
            notification.title = window.tOr('common.push_friend_request', sender.username + ' requested your friendship', {username: sender.username});
            notification.text = window.tOr('common.push_say_hi', "Say 'Hi'!");

            return notification;
        case 'LOGIN_NOTIFICATION':
            notification.title = window.tOr('common.push_user_online', sender.username + ' is online', {username: sender.username});
            notification.text = window.tOr('common.push_say_hi', "Say 'Hi'!");

            return notification;
        case 'LIKE_MESSAGE':
            // @todo
            break;
        case 'PHOTO_COMMENT':
            notification.url = '/app/photo?pid=' + data.target_id;
            notification.title = window.tOr('common.push_new_photo_comment', 'New photo comment from ' + sender.username, {username: sender.username});
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;
        case 'THREAD_NEW_MESSAGE':
            // The call surfaces already told both people about the call.
            if ((data.attachments || []).some((a) => a && a.type === 'call')) {
                document.dispatchEvent(new CustomEvent('thread:newmessage', {
                    detail: { thread_id: String(data.thread_id), box: threadBoxValue(entity.box) }
                }));
                return null;
            }
            notification.url = '/app/messages?thread_id=' + data.thread_id;
            notification.thread_id = data.thread_id;
            notification.title = window.tOr('common.push_new_message', 'New message from ' + sender.username, {username: sender.username});
            notification.text = messageNotificationText(data);

            // The box sits beside `data` on the frame, not inside the message
            // entity (MessageSocketNotification), unlike the moved frame below.
            document.dispatchEvent(new CustomEvent('thread:newmessage', {
                detail: { thread_id: String(data.thread_id), box: threadBoxValue(entity.box) }
            }));

            return notification;
        case 'THREAD_SYNC_DELETED':
            // A thread was deleted on another device, or removed because the
            // other user blocked us. Let the PM page drop the row in place.
            document.dispatchEvent(new CustomEvent('thread:deleted', {
                detail: { thread_id: String(data.thread_id) }
            }));
            return null;
        case 'THREAD_SYNC_MOVED':
            // A thread changed inbox box on another device (Message Requests,
            // #3039): accepted, or demoted to Requests. Let the PM page move it.
            document.dispatchEvent(new CustomEvent('thread:moved', {
                detail: {
                    thread_id: String(data.thread_id),
                    from_box: threadBoxValue(data.from_box),
                    to_box: threadBoxValue(data.to_box)
                }
            }));
            return null;
        case 'THREAD_VIEWED':
            // Reaches the reader's other devices and the contact. pm/polling.js
            // filters on viewer_uid. Not a notification.
            document.dispatchEvent(new CustomEvent('thread:viewed', {
                detail: {
                    thread_id: String(data.thread_id),
                    target_last_read_msg_id: String(data.target_last_read_msg_id || ''),
                    viewer_uid: String(data.viewer_uid || '')
                }
            }));
            return null;

        case 'THREAD_TYPING':
            document.dispatchEvent(new CustomEvent('thread:typing', {
                detail: { thread_id: String(data.thread_id), sender: sender }
            }));
            return null;

        case 'THREAD_MESSAGE_REACTION':
            // IDs kept as strings (they exceed Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:reaction', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id),
                    reactor_uid: String(data.reactor_uid),
                    reaction: data.reaction,
                    action: data.action,
                    reactions: data.reactions || []
                }
            }));
            return null;

        case 'THREAD_MESSAGE_RECALLED':
            // A message was unsent. Hand the PM page the ids so it can remove the
            // open bubble live. IDs stay strings (they exceed
            // Number.MAX_SAFE_INTEGER).
            document.dispatchEvent(new CustomEvent('thread:recalled', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id)
                }
            }));
            return null;

        case 'THREAD_MESSAGE_EDITED':
            // A message body was edited. Hand the PM page the server-rendered
            // body so it can swap the open bubble live. IDs stay strings (they
            // exceed Number.MAX_SAFE_INTEGER). `body` (raw editor prefill) is
            // present only on the sender's own channel.
            document.dispatchEvent(new CustomEvent('thread:edited', {
                detail: {
                    thread_id: String(data.thread_id),
                    message_id: String(data.message_id),
                    body_html: data.body_html || '',
                    body: (typeof data.body === 'string') ? data.body : null,
                    emoji_only: !!data.emoji_only,
                    edited_at: data.edited_at || ''
                }
            }));
            return null;

        case 'WALL_MESSAGE':
            notification.url = '/app/wall';
            notification.title = window.tOr('common.push_new_wall_post', 'New post on your wall');
            notification.img_url = data.thumb;
            notification.text = data.snippet ? data.snippet : data.message;

            return notification;

        // 1:1 call lifecycle events. These drive the call UI directly (ring
        // modal, in-call overlay, thread buttons), never a passive toast, so
        // each re-dispatches a ws:call-* CustomEvent and returns null. Payload
        // fields sit at the top level of the socket message (see
        // CallSocketNotification / AbstractCallAuthNotification). IDs stay strings.
        case 'CALL_RING':
            document.dispatchEvent(new CustomEvent('ws:call-ring', {
                detail: {
                    call: entity.call,
                    caller: entity.caller,
                    livekit_url: entity.livekit_url,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_CANCEL':
            document.dispatchEvent(new CustomEvent('ws:call-cancel', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    reason: entity.reason,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_ACCEPT':
            document.dispatchEvent(new CustomEvent('ws:call-accept', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_ANSWERED_ELSEWHERE':
            document.dispatchEvent(new CustomEvent('ws:call-answered-elsewhere', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_DECLINE':
            document.dispatchEvent(new CustomEvent('ws:call-decline', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_END':
            document.dispatchEvent(new CustomEvent('ws:call-end', {
                detail: {
                    call_id: entity.call_id ? String(entity.call_id) : null,
                    duration: entity.duration,
                    end_reason: entity.end_reason,
                    state_version: entity.state_version,
                }
            }));
            return null;

        case 'CALL_AUTH_REQUEST':
            document.dispatchEvent(new CustomEvent('ws:call-auth-request', {
                detail: {
                    userId: entity.sender && entity.sender.id ? String(entity.sender.id) : null,
                    sender: entity.sender,
                }
            }));
            return null;

        case 'CALL_AUTH_ACCEPTED':
            document.dispatchEvent(new CustomEvent('ws:call-auth-accepted', {
                detail: {
                    userId: entity.sender && entity.sender.id ? String(entity.sender.id) : null,
                    sender: entity.sender,
                }
            }));
            return null;

        // Senderless: the member whose call state changed is user_id. Without
        // it no thread can match, so the frame is dropped.
        case 'CALL_AUTH_CHANGED':
            if (entity.user_id) {
                document.dispatchEvent(new CustomEvent('calls:authorization-changed', {
                    detail: { userId: String(entity.user_id) }
                }));
            }
            return null;
    }

    return null;
}

window["escapeHtml"] = function (text) {
    var map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    };

    return text.replace(/[&<>"']/g, function (m) {
        return map[m];
    });
}

// Write a single count into one badge element: sets the capped text and toggles
// visibility. Templates render the badge even at zero (hidden inline) so the
// count can appear/increment without a page refresh. Visibility uses an
// !important inline display so it wins over the collapsed/expanded CSS rules;
// clearing it lets those class rules pick the right variant again.
window["writeCounterBadge"] = function (badge, count) {
    badge.setAttribute('data-count', String(Math.max(0, count)));

    if (count > 0) {
        var cap = parseInt(badge.getAttribute('data-counter-cap') || '0', 10);
        badge.textContent = (cap > 0 && count > cap) ? (cap + '+') : String(count);
        var label = badge.getAttribute('aria-label');
        if (label) {
            badge.setAttribute('aria-label', label.replace(/^\d+/, String(count)));
        }
        badge.style.removeProperty('display');
    } else {
        badge.textContent = '';
        badge.style.setProperty('display', 'none', 'important');
    }
};

// Update every navigation badge bound to a counter id (data-counter="<id>", e.g.
// 'pmNewCnt') to the new absolute value.
// data-counter-cap sets the optional "N+" ceiling (9 collapsed, 99 expanded).
window["syncCounterBadges"] = function (counterId, value) {
    var count = parseInt(value, 10);
    if (!Number.isFinite(count)) {
        return;
    }
    count = Math.max(0, count);

    document.querySelectorAll('[data-counter="' + counterId + '"]').forEach(function (badge) {
        writeCounterBadge(badge, count);
    });
};

function applyCounterUpdate(counterId, counterValue) {
    syncCounterBadges(counterId, counterValue);
    document.dispatchEvent(new CustomEvent('ws:counter-update', {
        detail: { id: counterId, value: counterValue }
    }));
}

window["onMessage"] = function (wsMessage) {
    let data;
    try {
        data = JSON.parse(wsMessage.data);

        if (!data || typeof data !== 'object') {
            throw new Error('Invalid message format');
        }
    } catch (e) {
        wsLog('Failed to parse WebSocket message', e);
        return;
    }

    // An RPC reply, never a typed event. A payload that only shares the id
    // shape falls through to the normal path.
    if (isTypingRpcReply(data)) {
        handleTypingRpcReply(data);

        return;
    }

    if (data.type === 'COUNTER_UPDATE') {
        // Server payload shape: { type: 'COUNTER_UPDATE', entity: { id, value } }
        const counterId = data.entity?.id;
        const counterValue = data.entity?.value;

        if (!counterId || typeof counterId !== 'string') {
            wsLog('Invalid COUNTER_UPDATE message - missing or invalid id');
            return;
        }

        if (TRACKED_COUNTER_IDS.includes(counterId)) {
            applyCounterUpdate(counterId, counterValue);
        }
    } else if (data.type === 'SYNC_TOTAL_COUNTER') {
        // Bulk paths such as mark-all-read send only this frame. pmNewCnt is the total of new messages.
        const newMessages = data.counters?.new_messages;
        if (Number.isFinite(newMessages)) {
            applyCounterUpdate('pmNewCnt', newMessages);
        }
    } else if (data.type === 'GLOBAL_FEED_POST_ADDED') {
        document.dispatchEvent(new CustomEvent('ws:global-feed-post-added', {
            detail: data,
        }));
    } else {
        const notification = prepareNotification(data);

        if (notification && window.enable_notifs) {
            window.showNotifications([notification])
        }
    }
}

// Names the token of the last page view that stopped reconnect, for every tab.
// The token stays in redux-state for REST, which refreshes it on a 401.
const WS_REFUSAL_STORAGE_KEY = 'ws-refused-token';
// A refused fresh token means a new mint will not help. Page views in this
// window skip the socket, so an outage costs no mint per page view.
const WS_REFUSAL_COOLDOWN_MS = 5 * 60 * 1000;
// Spreads the tabs that wait for the same cooldown end.
const WS_REFUSAL_JITTER_MS = 30 * 1000;

function withJitter(delayMs) {
    return delayMs + Math.floor(Math.random() * WS_REFUSAL_JITTER_MS);
}

/**
 * A tail of the JWT signature. It identifies the token and is no credential,
 * so the record can outlive a logout.
 * @param {string} token
 */
function wsTokenFingerprint(token) {
    return token.slice(-16);
}

/** @return {{fp: string, at: number}|null} A record dated in the future counts as none. */
function readWsRefusal() {
    try {
        const refusal = JSON.parse(safeStorageGet(WS_REFUSAL_STORAGE_KEY));

        return refusal && typeof refusal.fp === 'string' && Number.isFinite(refusal.at) && refusal.at <= Date.now()
            ? refusal
            : null;
    } catch {
        return null;
    }
}

// Local stacks proxy /v1/ws on the page host (.docker/nginx/nginx-conf.d/default.conf).
const LOCAL_WEB_HOST = /(^|\.)local(-\d+)?\.interpals\.net$/;
// Pairs with the api-<ref> host in devops/kubernetes ci-template/ingress.yaml.
const REVIEW_WEB_HOST = /^www-([a-z0-9-]+)\.review\.interpals\.dev$/;

/**
 * The /v1/ws URL for the stack that served the page. Only a production host
 * gets the production socket, so a test token never reaches it.
 *
 * @param {{hostname: string, host: string, protocol: string}} location
 * @return {string}
 */
function webSocketUrl(location) {
    var hostname = location.hostname;
    var review = REVIEW_WEB_HOST.exec(hostname);

    if (hostname === 'staging.interpals.net') {
        return 'wss://api-staging.interpals.net/v1/ws';
    }
    if (review) {
        return 'wss://api-' + review[1] + '.review.interpals.dev/v1/ws';
    }
    // No production stack serves pages on interpals.dev.
    if (hostname === 'localhost' || LOCAL_WEB_HOST.test(hostname)
        || hostname === 'interpals.dev' || hostname.endsWith('.interpals.dev')) {
        return (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/v1/ws';
    }

    return 'wss://api.interpals.net/v1/ws';
}

$(document).ready(function () {
    var retryCount = 0;
    var tokenRetryCount = 0;
    var isConnecting = false;
    var currentWs = null;
    // A 403 on /v1/ws reaches the browser only as a close before `open`, the
    // same as an outage. Until a socket has opened in this page view, such a
    // close replaces the token once, and a second one stops reconnect.
    var suspectToken = null;
    var everOpened = false;
    var stoppedForPageView = false;
    // Bumped by cleanup, so a connect still awaiting its token is dropped.
    var generation = 0;
    // One pending connect at most, so a resume never adds a second retry chain.
    var connectTimer = null;

    function scheduleConnect(fn, delayMs) {
        clearTimeout(connectTimer);
        connectTimer = setTimeout(function () {
            connectTimer = null;
            fn();
        }, delayMs);
    }

    function cancelScheduledConnect() {
        clearTimeout(connectTimer);
        connectTimer = null;
    }

    function scheduleReconnect() {
        var retryTimeout = Math.min(Math.pow(2, retryCount), MAX_RETRY_DELAY);
        retryCount++;

        wsLog('[WebSocket] Retrying in ' + retryTimeout + ' seconds');

        scheduleConnect(connect, retryTimeout * 1000);
    }

    /** @return {boolean} True when the failure calls for a scheduled reconnect. */
    function shouldRetryAfterFailureBeforeOpen(sentToken) {
        // The online listener reconnects.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
            return false;
        }

        // After an open in this page view, treated as an outage.
        if (everOpened) {
            return true;
        }

        var firstRefusal = suspectToken === null;
        suspectToken = sentToken;
        if (firstRefusal) {
            return true;
        }

        wsLog('[WebSocket] A fresh token was refused too, stopping for this page view');
        stoppedForPageView = true;
        safeStorageSet(WS_REFUSAL_STORAGE_KEY, JSON.stringify({ fp: wsTokenFingerprint(sentToken), at: Date.now() }));
        scheduleConnect(resume, withJitter(WS_REFUSAL_COOLDOWN_MS));
        return false;
    }

    /** online and a bfcache restore lift a stop. The suspect token stays suspect. */
    function resume() {
        cancelScheduledConnect();
        stoppedForPageView = false;
        retryCount = 0;
        tokenRetryCount = 0;
        connect();
    }

    var connect = async function () {
        if (stoppedForPageView) {
            wsLog('[WebSocket] Stopped for this page view, skipping');
            return false;
        }

        if (isConnecting || currentWs) {
            wsLog('[WebSocket] Already connecting or connected, skipping');
            return false;
        }

        // Check if user is logged in by looking for user-id meta tag
        const userIdMeta = document.querySelector('meta[name="user-id"]');
        if (!userIdMeta || !userIdMeta.getAttribute('content')) {
            wsLog('[WebSocket] Not logged in, skipping connection');
            return false;
        }

        wsLog('[WebSocket] Starting connection attempt');
        isConnecting = true;

        var wspath = webSocketUrl(document.location);

        var connectGeneration = generation;
        var tokenData;
        if (suspectToken !== null) {
            tokenData = await refreshJwtToken(suspectToken);
        } else {
            tokenData = await initializeJwtToken();
            var storedToken = tokenData?.auth?.token;
            // A page that has opened a socket treats every later failure as an outage.
            var refusal = everOpened ? null : readWsRefusal();
            if (connectGeneration === generation && refusal && storedToken && refusal.fp === wsTokenFingerprint(storedToken)) {
                var cooldownLeft = refusal.at + WS_REFUSAL_COOLDOWN_MS - Date.now();
                if (cooldownLeft > 0) {
                    wsLog('[WebSocket] Token refused by an earlier page view, waiting');
                    isConnecting = false;
                    stoppedForPageView = true;
                    scheduleConnect(resume, withJitter(cooldownLeft));
                    return false;
                }
                // Counts as the first refusal, so one more stops reconnect.
                suspectToken = storedToken;
                tokenData = await refreshJwtToken(suspectToken);
            }
        }
        if (connectGeneration !== generation) {
            return false;
        }
        if (!tokenData || !tokenData.auth || !tokenData.auth.token) {
            isConnecting = false;
            if (tokenRetryCount >= MAX_TOKEN_RETRIES) {
                wsLog('[WebSocket] Token not available after max retries, giving up');
                return false;
            }
            wsLog('[WebSocket] No token available yet, retrying in 1 second');
            tokenRetryCount++;
            scheduleConnect(connect, 1000);
            return false;
        }

        tokenRetryCount = 0;
        var sentToken = tokenData.auth.token;
        wspath += '?token=' + sentToken;

        var ws = new WebSocket(wspath);
        var opened = false;
        currentWs = ws;

        ws.onopen = function () {
            opened = true;
            everOpened = true;
            suspectToken = null;
            retryCount = 0;
            isConnecting = false;
            if (safeStorageGet(WS_REFUSAL_STORAGE_KEY) !== null) {
                safeStorageRemove(WS_REFUSAL_STORAGE_KEY);
            }
            wsLog('[WebSocket] Connected successfully');
            subscribeToRequestedChannels(ws);
            document.dispatchEvent(new CustomEvent('ws:connected'));
        };

        ws.onmessage = onMessage;

        ws.onclose = function (event) {
            // Closed by cleanup, which owns the state from there.
            if (currentWs !== ws) {
                return;
            }

            currentWs = null;
            isConnecting = false;
            document.dispatchEvent(new CustomEvent('ws:disconnected'));

            wsLog('[WebSocket] Connection closed (code: ' + event.code + ', opened: ' + opened + ')');

            if (opened || shouldRetryAfterFailureBeforeOpen(sentToken)) {
                scheduleReconnect();
            }
        };

        ws.onerror = function (error) {
            wsLog('[WebSocket] Connection error', error);
            if (currentWs === ws) {
                isConnecting = false;
            }
        };
    };

    function cleanup() {
        generation++;
        cancelScheduledConnect();
        if (currentWs) {
            wsLog('[WebSocket] Cleaning up connection');
            var ws = currentWs;
            currentWs = null;
            ws.close(1000, 'Page unload');
            document.dispatchEvent(new CustomEvent('ws:disconnected'));
        }
        isConnecting = false;
        retryCount = 0;
        tokenRetryCount = 0;
    }

    // Not beforeunload: it also fires when the unload is cancelled (the call
    // leave prompt, a download), which would leave the page without a socket.
    window.addEventListener('pagehide', cleanup);

    window.addEventListener('pageshow', function (event) {
        if (event.persisted) {
            resume();
        }
    });

    // Coming back online means earlier handshake failures were the network.
    window.addEventListener('online', resume);

    // A channel requested while this tab's socket is already open is subscribed
    // live. A channel requested before connect is covered by the
    // requestedWebSocketChannels set, which onopen flushes via
    // subscribeToRequestedChannels.
    document.addEventListener('ws:subscribe-channel-request', function (event) {
        const channel = event?.detail?.channel;

        if (channel && currentWs && currentWs.readyState === WebSocket.OPEN) {
            subscribeToChannel(currentWs, channel);
        }
    });

    // The sender lives here because currentWs does. A frame is never queued: an
    // RPC names a moment that has passed, so the caller is told it did not go out.
    window.sendWebSocketRpc = function (payload) {
        return sendRpcFrame(currentWs, payload);
    };

    connect();
});
